import { ENV } from "@/constants/envConfig";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { gdSlsReportError } from "./gdSlsRum";
import { uidFieldName } from "./uid";

const VERASION = 1;

/**
 * @typedef {"pin"|"board"|"user"} DbType 记录表类型
 * @typedef {{id:string|number, record_date:string, record_time:number}} Record 记录表类型
 */

/**
 * 历史记录
 * 基于idb https://github.com/jakearchibald/idb
 */
export class HistoryRecordDB {
  constructor({ tables, userId, env, version }) {
    if (typeof window === "undefined") {
    } else {
      this.MAX = 30; // 存储最大天数
      this.ONEDAY = 24 * 3600 * 1000; // 存一下一天的时间，避免重复计算
      this.createConnection({ tables, userId, env, version }); //建立indexdb连接
    }
  }

  /**
   * 建立链接
   * @param {{ tables, userId, env, version }} param0
   */
  createConnection({ tables, userId, env, version }) {
    const initDb = async () => {
      try {
        const { openDB } = await import("idb");
        this.db = await openDB(userId + env + "_history_db", version, {
          upgrade(db) {
            // 创建表
            for (let i = 0; i < tables.length; i++) {
              const { name, indexs } = tables[i];

              if (db.objectStoreNames.contains(name)) {
                // 已创建情况处理
                continue;
              }
              const objectStore = db.createObjectStore(name, {
                keyPath: ["id", "record_date"],
              });

              objectStore.createIndex("record_time", "record_time", {
                unique: true,
              });

              // 创建索引
              if (Array.isArray(indexs)) {
                for (let j = 0; j < indexs.length; j++) {
                  const index = indexs[j];
                  objectStore.createIndex(index.name, index.keyPath, {
                    unique: index.unique || false,
                    // 数组拆分索引
                    multiEntry: index.multiEntry || false,
                  });
                }
              }
            }
          },
        });
      } catch (error) {
        console.error(error);
        this.fail = true; //连接失败标识
      }
    };

    if (this.db) {
      this.db.close();
      this.db = null;
    }

    this.initPromise = initDb();

    this.reconnect = () => {
      this.createConnection({ tables, userId, env, version });
    };
  }

  /**
   * close db
   */
  close() {
    if (this.db) {
      this.db.close();
    }
  }

  /**
   * add one record
   * @param @param {{
   * type: DbType
   * record: Record
   * id: number|string
   * }} params
   * @returns
   */
  async add({ type, record, id }) {
    try {
      await this.initPromise;
      if (this.fail) return;

      if (!this.db) {
        this.reconnect();
        await this.initPromise;
      }

      const record_date = dayjs().format("YYYY-MM-DD");

      const tx = this.db.transaction(type, "readwrite"); //创建事务
      const store = tx.objectStore(type); //获取表
      // add record 添加记录
      const key = await store.put({
        ...record,
        id,
        record_date,
        record_time: Date.now(),
      });

      await tx.done;

      await this.delOverflow(type); //删除溢出数据

      return key;
    } catch (error) {
      // 处理连接关闭错误
      if (error.message?.includes("The database connection is closing")) {
        this.reconnect();
        return this.add({ type, record, id });
      }

      // 避免影响主流程
      console.error(error);
      const message = "history record add error：" + error.message;
      gdSlsReportError(new Error(message));
    }
  }

  /**
   * 删除指定数据
   * @param {{
   * type: DbType
   * lastRecord: Record
   * }} params
   * @returns
   */
  async del({ type, record }) {
    if (this.fail) return;

    const tx = this.db.transaction(type, "readwrite");
    const store = tx.objectStore(type);

    const readyDelKey = await store
      .index("record_time")
      .getKey(record.record_time);
    await store.delete(readyDelKey);
    await tx.done;
  }

  /**
   * 删除溢出数据（超过30天的数据）
   * @param {DbType} type
   */
  async delOverflow(type) {
    const minTime = dayjs(
      dayjs().subtract(this.MAX, "day").format("YYYY-MM-DD")
    ).valueOf(); // 超过30天的时间
    const tx = this.db.transaction(type, "readwrite");

    const store = tx.objectStore(type);
    const timeIndex = store.index("record_time");
    // IDBKeyRange 可以参考 https://developer.mozilla.org/en-US/docs/Web/API/IDBKeyRange
    const readyDelKeys = await timeIndex.getAllKeys(
      IDBKeyRange.upperBound(minTime, true)
    );

    if (readyDelKeys) {
      for (let i = 0; i < readyDelKeys.length; i++) {
        const key = readyDelKeys[i];
        await store.delete(key);
      }
    }

    await tx.done;
  }

  /**
   * 清空指定的历史记录表
   */
  async delAll(type) {
    await this.initPromise;
    if (this.fail) return;
    await this.db.clear(type);
  }
  /**
   * 获取记录表数据，按时间降序查询
   * @param {{
   * type: DbType
   * lastRecord: Record
   * limit?: number
   * }} params
   * @returns
   */
  async getList({ type, lastRecord, limit }) {
    await this.initPromise;
    if (this.fail) return { err: 500 };
    const tx = this.db.transaction(type, "readonly");
    const timeIndex = tx.objectStore(type).index("record_time");

    const lastRecordTime = lastRecord?.record_time || Date.now();
    const result = [];

    let cursor = await timeIndex.openCursor(
      IDBKeyRange.upperBound(lastRecordTime, true),
      "prev"
    );

    while (cursor) {
      result.push(cursor.value);
      if (result.length == limit) {
        break;
      }
      cursor = await cursor.continue();
    }

    await tx.done;

    return result;
  }
}

const HistoryTables = [
  {
    name: "pin",
  },
];

// 数据库初始化
const histotyDbService = new HistoryRecordDB({
  userId: Cookies.get(uidFieldName),
  tables: HistoryTables,
  env:
    ENV === "prod"
      ? ""
      : ENV,
  version: VERASION,
});

histotyDbService.changeUser = function (user) {
  histotyDbService.createConnection({
    userId: user?.user_id || Cookies.get(uidFieldName),
    tables: HistoryTables,
    env:
      ENV === "prod"
        ? ""
        : ENV,
    version: VERASION,
  });
};

export default histotyDbService;
